// src/hooks/useRoles.js
// Maneja roles de usuario: admin o asesor
import { useState, useEffect } from "react";
import { doc, getDoc, setDoc, collection, onSnapshot } from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { db, auth } from "../firebase";

// Permisos por rol
export const PERMISOS = {
  admin: {
    verProveedores:  true,
    verUsuarios:     true,
    verReportes:     true,
    puedeEliminar:   true,
    puedeCrearUsers: true,
    verTodosClientes:true,
    verPedidos:      true,
    verTransporte:   true,
    verMarketing:    true,
    verVentas:       true,
    verMonitoreo:    true,
  },
  asesor: {
    verProveedores:   false,
    verUsuarios:      false,
    verReportes:      false,
    puedeEliminar:    false,
    puedeCrearUsers:  false,
    verTodosClientes: false,
    verPedidos:       true,
    verTransporte:    true,
    verMarketing:     false,
    verVentas:        true,
    verMonitoreo:     false,
  },
};

// Hook principal — obtiene el rol del usuario actual
export function useRol() {
  const [rol,     setRol]     = useState(null);
  const [perfil,  setPerfil]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    getDoc(doc(db, "usuarios", user.uid)).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setRol(data.rol || "asesor");
        setPerfil(data);
      } else {
        // Si no tiene perfil, asignar asesor por defecto
        setRol("asesor");
        setPerfil({ nombre: user.email, email: user.email, rol: "asesor" });
      }
      setLoading(false);
    });
  }, []);

  const permisos = PERMISOS[rol] || PERMISOS.asesor;
  const esAdmin  = rol === "admin";

  return { rol, perfil, loading, permisos, esAdmin };
}

// Hook para gestionar todos los usuarios (solo admin)
export function useUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "usuarios"), snap => {
      setUsuarios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  // Crear nuevo usuario (admin + asesor)
  const crearUsuario = async ({ nombre, email, password, rol, telefono }) => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, "usuarios", cred.user.uid), {
        nombre,
        email,
        rol,
        telefono: telefono || "",
        activo: true,
        creadoEn: new Date().toISOString(),
      });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };

  // Actualizar rol o datos de usuario
  const actualizarUsuario = async (uid, datos) => {
    await setDoc(doc(db, "usuarios", uid), datos, { merge: true });
  };

  // Desactivar usuario (no borra, solo desactiva)
  const desactivarUsuario = async (uid) => {
    await setDoc(doc(db, "usuarios", uid), { activo: false }, { merge: true });
  };

  return { usuarios, loading, crearUsuario, actualizarUsuario, desactivarUsuario };
}
